import type { TotpMfaReadiness } from './authService';
import { UserRole, UserStatus, type UserSecurityNudges } from '../types/firestore';
import {
  canAccessServiceDashboard,
  getUserRole,
  getUserRoleIdentity,
  getUserStatus,
  type AccessClaims,
  type AccessProfile,
} from '../utils/accessControl';

export const TOTP_NUDGE_VERSION = 'totp-nudge-v1';
export const TOTP_NUDGE_SNOOZE_DAYS = 14;
export const TOTP_NUDGE_SNOOZE_MS = TOTP_NUDGE_SNOOZE_DAYS * 24 * 60 * 60 * 1000;

export type TotpNudgeAudience = 'client' | 'serviceAdmin' | 'admin';

export interface TotpNudgeCopy {
  title: string;
  description: string;
  helper: string;
}

export interface TotpNudgeDecision {
  shouldShow: boolean;
  audience: TotpNudgeAudience;
  reason:
    | 'eligible'
    | 'missing-user'
    | 'not-active'
    | 'service-not-approved'
    | 'totp-active'
    | 'totp-unavailable'
    | 'snoozed'
    | 'unsupported-role';
}

type TotpNudgeProfile = AccessProfile & {
  securityNudges?: UserSecurityNudges | null;
};

const toMillis = (value: unknown): number | null => {
  if (!value) return null;
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (
    typeof value === 'object' &&
    typeof (value as { toMillis?: unknown }).toMillis === 'function'
  ) {
    return (value as { toMillis: () => number }).toMillis();
  }
  if (typeof value === 'object' && typeof (value as { seconds?: unknown }).seconds === 'number') {
    return (value as { seconds: number }).seconds * 1000;
  }
  return null;
};

export const getTotpNudgeCopy = (audience: TotpNudgeAudience): TotpNudgeCopy => {
  if (audience === 'serviceAdmin') {
    return {
      title: 'Zabezpiecz konto serwisu',
      description:
        'To konto może obsługiwać zgłoszenia klientów. Weryfikacja dwuetapowa utrudnia dostęp osobie, która zna samo hasło.',
      helper: 'To opcjonalne zabezpieczenie. Możesz je później wyłączyć w ustawieniach.',
    };
  }

  if (audience === 'admin') {
    return {
      title: 'Włącz 2FA dla konta administracyjnego',
      description:
        'Konto z dostępem administracyjnym powinno mieć dodatkowe potwierdzenie logowania.',
      helper: 'To opcjonalne zabezpieczenie. Możesz je później wyłączyć w ustawieniach.',
    };
  }

  return {
    title: 'Zabezpiecz konto w 2 minuty',
    description:
      'Włącz weryfikację dwuetapową, żeby przy logowaniu oprócz hasła wymagany był też kod z aplikacji uwierzytelniającej.',
    helper: 'To opcjonalne zabezpieczenie. Możesz je później wyłączyć w ustawieniach.',
  };
};

export const getTotpNudgeTarget = (audience: TotpNudgeAudience): string => {
  const workspace = audience === 'serviceAdmin' ? '&workspace=service' : '';
  return `/app/settings?section=security&action=enable-2fa${workspace}`;
};

export const resolveTotpNudgeDecision = (
  profile: TotpNudgeProfile | null | undefined,
  claims: AccessClaims | null | undefined,
  readiness: TotpMfaReadiness,
  options: { now?: number; ignoreSnooze?: boolean } = {}
): TotpNudgeDecision => {
  const now = options.now ?? Date.now();
  const role = getUserRole(profile, claims);
  const roleIdentity = getUserRoleIdentity(profile);
  const status = getUserStatus(profile, claims) || profile?.status || null;
  const audience: TotpNudgeAudience =
    claims?.isAdmin === true || role === UserRole.ADMIN
      ? 'admin'
      : roleIdentity === UserRole.SERVICE_ADMIN
        ? 'serviceAdmin'
        : 'client';

  if (!profile) {
    return { shouldShow: false, audience, reason: 'missing-user' };
  }

  if (status !== UserStatus.ACTIVE) {
    return { shouldShow: false, audience, reason: 'not-active' };
  }

  if (audience === 'serviceAdmin' && !canAccessServiceDashboard(profile, claims)) {
    return { shouldShow: false, audience, reason: 'service-not-approved' };
  }

  if (readiness.hasTotpEnrollment || readiness.status === 'active') {
    return { shouldShow: false, audience, reason: 'totp-active' };
  }

  if (readiness.status !== 'not-enrolled') {
    return { shouldShow: false, audience, reason: 'totp-unavailable' };
  }

  const nudges = profile.securityNudges as UserSecurityNudges | null | undefined;
  const snoozedUntil = toMillis(nudges?.totpNudgeSnoozedUntil);
  if (!options.ignoreSnooze && nudges?.totpNudgeVersion === TOTP_NUDGE_VERSION && snoozedUntil) {
    if (now < snoozedUntil) {
      return { shouldShow: false, audience, reason: 'snoozed' };
    }
  }

  return { shouldShow: true, audience, reason: 'eligible' };
};
